/**
 * 営業日数計算ユーティリティ（土日・日本の祝日を除く）
 * 祝日リスト対象年: 2024 〜 2028
 */

const HOLIDAYS = new Set([
  // ── 2024 ──
  '2024-01-01', // 元日
  '2024-01-08', // 成人の日
  '2024-02-11', // 建国記念の日（日）
  '2024-02-12', // 振替休日
  '2024-02-23', // 天皇誕生日
  '2024-03-20', // 春分の日
  '2024-04-29', // 昭和の日
  '2024-05-03', // 憲法記念日
  '2024-05-04', // みどりの日
  '2024-05-05', // こどもの日（日）
  '2024-05-06', // 振替休日
  '2024-07-15', // 海の日
  '2024-08-11', // 山の日（日）
  '2024-08-12', // 振替休日
  '2024-09-16', // 敬老の日
  '2024-09-22', // 秋分の日（日）
  '2024-09-23', // 振替休日
  '2024-10-14', // スポーツの日
  '2024-11-03', // 文化の日（日）
  '2024-11-04', // 振替休日
  // 11/23 勤労感謝の日は土曜のため振替なし
  // ── 2025 ──
  '2025-01-01', // 元日
  '2025-01-13', // 成人の日
  '2025-02-11', // 建国記念の日
  '2025-02-23', // 天皇誕生日（日）
  '2025-02-24', // 振替休日
  '2025-03-20', // 春分の日
  '2025-04-29', // 昭和の日
  '2025-05-03', // 憲法記念日
  '2025-05-04', // みどりの日（日）
  '2025-05-05', // こどもの日
  '2025-05-06', // 振替休日（5/4の振替）
  '2025-07-21', // 海の日
  '2025-08-11', // 山の日
  '2025-09-15', // 敬老の日
  '2025-09-23', // 秋分の日
  '2025-10-13', // スポーツの日
  '2025-11-03', // 文化の日
  '2025-11-23', // 勤労感謝の日（日）
  '2025-11-24', // 振替休日
  // ── 2026 ──
  '2026-01-01', // 元日
  '2026-01-12', // 成人の日
  '2026-02-11', // 建国記念の日
  '2026-02-23', // 天皇誕生日
  '2026-03-20', // 春分の日
  '2026-04-29', // 昭和の日
  '2026-05-03', // 憲法記念日（日）
  '2026-05-04', // みどりの日
  '2026-05-05', // こどもの日
  '2026-05-06', // 振替休日（5/3の振替）
  '2026-07-20', // 海の日
  '2026-08-11', // 山の日
  '2026-09-21', // 敬老の日
  '2026-09-23', // 秋分の日
  '2026-10-12', // スポーツの日
  '2026-11-03', // 文化の日
  '2026-11-23', // 勤労感謝の日
  // ── 2027 ──
  '2027-01-01', // 元日
  '2027-01-11', // 成人の日
  '2027-02-11', // 建国記念の日
  '2027-02-23', // 天皇誕生日
  '2027-03-21', // 春分の日（日）
  '2027-03-22', // 振替休日
  '2027-04-29', // 昭和の日
  '2027-05-03', // 憲法記念日
  '2027-05-04', // みどりの日
  '2027-05-05', // こどもの日
  '2027-07-19', // 海の日
  '2027-08-11', // 山の日
  '2027-09-20', // 敬老の日
  '2027-09-23', // 秋分の日
  '2027-10-11', // スポーツの日
  '2027-11-03', // 文化の日
  '2027-11-23', // 勤労感謝の日
  // ── 2028 ──
  // 1/1 元日は土曜のため振替なし
  '2028-01-01', // 元日
  '2028-01-10', // 成人の日
  '2028-02-11', // 建国記念の日
  '2028-02-23', // 天皇誕生日
  '2028-03-20', // 春分の日
  // 4/29 昭和の日は土曜のため振替なし
  '2028-04-29', // 昭和の日
  '2028-05-03', // 憲法記念日
  '2028-05-04', // みどりの日
  '2028-05-05', // こどもの日
  '2028-07-17', // 海の日
  '2028-08-11', // 山の日
  '2028-09-17', // 敬老の日
  '2028-09-22', // 秋分の日
  '2028-10-08', // スポーツの日
  '2028-11-03', // 文化の日
  '2028-11-23', // 勤労感謝の日
]);

/** Date → "YYYY-MM-DD" */
function toKey(d) {
  return (
    d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0')
  );
}

/** 対象日が営業日（平日かつ祝日でない）か判定 */
function isBusinessDay(d) {
  const dow = d.getDay();
  if (dow === 0 || dow === 6) return false; // 日・土
  return !HOLIDAYS.has(toKey(d));
}

/**
 * 今日から targetDate までの営業日数を返す。
 *   正の値 → 未来（残営業日数）
 *   0     → 当日
 *   負の値 → 過去
 *
 * @param {string | Date} targetDate - "YYYY-MM-DD" 文字列 または Date オブジェクト
 * @returns {number | null}
 */
export function businessDaysUntil(targetDate) {
  if (!targetDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0);

  if (today.getTime() === target.getTime()) return 0;

  const sign = target > today ? 1 : -1;
  const [from, to] = sign === 1 ? [today, target] : [target, today];

  let count = 0;
  const cur = new Date(from);
  cur.setDate(cur.getDate() + 1); // 今日は含めず翌日から
  while (cur <= to) {
    if (isBusinessDay(cur)) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return sign * count;
}
